/* useUpdateMenu.tsx */
import Features from '../../../types/enums/features';
import Focus from '../../../types/enums/focus';
import IMenuItem from '../../../types/interfaces/menuItem';
import MenuData from '../menu/menuData';
import Pages from '@pages';
import Plan from '../../../classes/plan';
import User from '../../../classes/user';
import { focusSelector } from '../../../store/slices/focus.slice';
import { routerSelector } from '../../../store/slices/router.slice';
import { store } from '@shared/store/store';
import { useAppSelector } from '../../../store/hooks';
import { useEffect, useState } from 'react';
import { userSelector } from '../../../store/slices/user.slice';

/**
 * Hook to update the menu information
 */
export default function useUpdateMenu(display: boolean): {
  data: IMenuItem[];
  focused: boolean;
  index: number;
  stacked: boolean;
} {
  const { focus, stack } = useAppSelector(focusSelector);
  const { route } = useAppSelector(routerSelector);
  const { initialized, operator, planId } = useAppSelector(userSelector);

  const [data, setData] = useState<IMenuItem[]>([]);
  const [index, setIndex] = useState(0);

  const focused = focus === Focus.MENU;
  const stacked = [Focus.LOGIN, Focus.MENU].some((item) =>
    stack.includes(item)
  );

  const getData = (plan?: Plan) => {
    const { app } = store.getState();
    const data: IMenuItem[] = [];
    Object.values(MenuData).map((item) => {
      let addItem = true;
      switch (item) {
        case MenuData.Movies:
        case MenuData.Series:
        case MenuData.Shows:
          addItem = !app.status.anonymous
            ? plan?.features?.includes(Features.LAST_SHOWS) || false
            : true;
          break;
        case MenuData.Recordings:
          addItem =
            !(operator?.mySpaceEnabled ?? false) &&
            (!app.status.anonymous
              ? plan?.features?.includes(Features.ALLOW_RECORD) ?? false
              : true);
          break;
        case MenuData.MoreContent:
          addItem = operator?.moreContentEnabled ?? false;
          break;
        case MenuData.MySpace:
          addItem = operator?.mySpaceEnabled ?? false;
          break;
      }
      addItem && data.push(item);
    });
    return data;
  };

  const getIndex = (data: IMenuItem[]) => {
    switch (route.Page) {
      case Pages.INITIAL:
        return data.indexOf(MenuData.Initial);
      case Pages.CHANNELS_MAIN:
      case Pages.CLASSIC_CHANNEL:
        return data.indexOf(MenuData.Channels);
      case Pages.RECORDINGS:
        return data.indexOf(MenuData.Recordings);
      case Pages.MY_SPACE:
        return data.indexOf(MenuData.MySpace);
      case Pages.MOVIES:
        return data.indexOf(MenuData.Movies);
      case Pages.SERIES_U7D:
        return data.indexOf(MenuData.Series);
      case Pages.SHOWS_U7D:
        return data.indexOf(MenuData.Shows);
      case Pages.MORE_CONTENT:
        return data.indexOf(MenuData.MoreContent);
      case Pages.SETTINGS:
        return data.indexOf(MenuData.Settings);
      case Pages.EXIT:
        return data.indexOf(MenuData.Exit);
    }
  };

  useEffect(() => {
    if (initialized) {
      const plan = User.getPlan(planId);
      const data = getData(plan);
      const index = getIndex(data);
      setData(data);
      setIndex((prev) => index ?? prev);
    }
  }, [display, focus, route, initialized, operator, planId]);

  return { data, focused, index, stacked };
}
